import axios from 'axios';

// Use relative URL so Create React App proxies /api/* to the backend dev server
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['X-Auth-Token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('authToken');
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      // Only redirect on 401 for authenticated requests, not the login endpoint
      if (token && !isLoginRequest) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  
  verifyToken: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },
};

// Public API
export const publicAPI = {
  trackPackage: async (trackingNumber) => {
    const response = await api.get(`/track/${trackingNumber}`);
    return response.data;
  },
  
  getPackageStatus: async (trackingNumber) => {
    const response = await api.get(`/track/${trackingNumber}/status`);
    return response.data;
  },
  
  submitContactForm: async (formData) => {
    const response = await api.post('/contact', formData);
    return response.data;
  },
  
  getCompanyInfo: async () => {
    const response = await api.get('/company-info');
    return response.data;
  },
  
  getServiceAreas: async () => {
    const response = await api.get('/service-areas');
    return response.data;
  },
  
  calculateRate: async (rateData) => {
    const response = await api.post('/calculate-rate', rateData);
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  },
  
  getShipments: async (params = {}) => {
    const response = await api.get('/admin/shipments', { params });
    return response.data;
  },
  
  getShipment: async (id) => {
    const response = await api.get(`/admin/shipments/${id}`);
    return response.data;
  },
  
  createShipment: async (shipmentData) => {
    const response = await api.post('/admin/shipments', shipmentData);
    return response.data;
  },
  
  updateShipmentStatus: async (id, statusData) => {
    const response = await api.put(`/admin/shipments/${id}/status`, statusData);
    return response.data;
  },
  
  deleteShipment: async (id) => {
    const response = await api.delete(`/admin/shipments/${id}`);
    return response.data;
  },
  
  holdShipment: async (id, reason, holdType, facilityName) => {
    const response = await api.put(`/admin/shipments/${id}/hold`, { reason, holdType, facilityName });
    return response.data;
  },
  
  releaseShipment: async (id) => {
    const response = await api.put(`/admin/shipments/${id}/release`);
    return response.data;
  },
  
  updateTrackingEvent: async (shipmentId, historyId, eventData) => {
    const response = await api.put(`/admin/shipments/${shipmentId}/tracking/${historyId}`, eventData);
    return response.data;
  },
  
  deleteTrackingEvent: async (shipmentId, historyId) => {
    const response = await api.delete(`/admin/shipments/${shipmentId}/tracking/${historyId}`);
    return response.data;
  },
};

export default api;
